import React, { useEffect, useState } from "react";
import { useUserServices } from "../context/DealerProfile";
import { TableIcon, CreditCard } from "lucide-react";
import AddressList from "../features/Address/AddressList";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal } from "flowbite-react";
import AddressForm from "../features/Address/AddressForm";
import toast from "react-hot-toast";
import { mutate } from "swr";

const BillingAddressesPage = () => {
  const { dealerDetail } = useUserServices();
  const { billingAddresses, accountName, dealerName } = dealerDetail || {};
  const [viewMode, setViewMode] = useState("table");
  const [isModalOpen, setIsModalOpen] = useState(false);
  // console.log({ dealerDetail });
  const [formData, setFormData] = useState({
    Company_Name: "",
    Address_2: "",
    City: "",
    Postal_Code: "",
    Country: "",
    State: "",
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate(); // Get navigate function
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const query = queryParams.get("query");

    if (query === "create") {
      setIsModalOpen(true);
    }
  }, [location.search]);

  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  const handleAddBillingAddressClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple validation logic
    const newErrors = {};
    if (!formData.Company_Name)
      newErrors.Company_Name = "Company Name is required.";
    if (!formData.Country)
      newErrors.Country = "Country is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare the data to send to the API
    const apiData = {
      Company_Name: formData.Company_Name,
      Address_2: formData.Address_2,
      City: formData.City,
      Postal_Code: formData.Postal_Code,
      Country: formData.Country,
      State: formData.State,
      Name: formData.Company_Name,
    };

    if (accountName?.id) apiData["Channel_Partner"] = accountName?.id;
    if (dealerName?.id) apiData["Contacts"] = dealerName?.id;

    // Show loading toast
    const loadingToast = toast.loading("Creating address...");

    try {
      // Send the form data to the API
      const response = await fetch(
        "/server/dealer_backend/create-address?addressType=Billing&customerType=inHouse",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiData),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      await response.json();
      // const result = await response.json();
      // console.log("Success:", result);

      // Show success toast
      toast.success("Address created successfully!", {
        id: loadingToast, // Use the same ID to remove the loading toast
      });

      mutate("/user");
      // Optionally, navigate to another page or show a success message
      const queryParams = new URLSearchParams(location.search);
      const historyUrl = queryParams.get("history");
      if (historyUrl) {
        navigate(historyUrl);
      } else {
        setFormData({
          Company_Name: "",
          Address_2: "",
          City: "",
          Postal_Code: "",
          Country: "",
          State: "",
        });
        setIsModalOpen(false);
        navigate("/billing-adresses"); // fallback to billing addresses page
      }
    } catch (error) {
      console.error("Error:", error);
      // Show error toast
      toast.error("Failed to create address. Please try again.", {
        id: loadingToast, // Use the same ID to remove the loading toast
      });
    }
  };

  return (
    <div className="px-4 max-w-5xl mx-auto">
      <div className="flex justify-between my-4">
        <h2 className="text-2xl font-bold text-gray-700">Billing Addresses</h2>
        <Button onClick={handleAddBillingAddressClick}>
          Add Billing Address
        </Button>
      </div>
      <div className="inline-flex gap-x-2 bg-slate-200 shadow-md rounded-md p-2 items-center mb-4">
        <button
          onClick={() => toggleViewMode("table")}
          className={`flex items-center rounded-md p-2 ${
            viewMode === "table" ? "bg-blue-500 text-white" : "bg-gray-200"
          }`}
        >
          <TableIcon className="h-5 aspect-square" />
        </button>
        <button
          onClick={() => toggleViewMode("card")}
          className={`flex items-center rounded-md p-2 ${
            viewMode === "card" ? "bg-blue-500 text-white" : "bg-gray-200"
          }`}
        >
          <CreditCard className="h-5 aspect-square" />
        </button>
      </div>

      <AddressList
        addresses={billingAddresses}
        viewMode={viewMode}
        type="Billing"
      />

      {/* Modal for Adding Billing Address */}
      <Modal show={isModalOpen} onClose={handleModalClose}>
        <Modal.Header>Add Billing Address</Modal.Header>
        <Modal.Body>
          <AddressForm
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BillingAddressesPage;
